"use client";

import { VimeoPhoneVideo } from "@/components/educators/components/VimeoPhoneVideo";
import {
  type EducatorVideoTranscript,
  GARCIA_VIDEO_TRANSCRIPT,
} from "@/components/educators/data/video-transcripts";
import { SectionFrame } from "@/components/ui/section-frame";
import Link from "next/link";

type VoiceCard = {
  name: string;
  headline: string;
  blurb: string;
  videoSrc: string;
  videoTitle: string;
  phoneContainerClassName?: string;
  badgeSrc?: string;
  badgeClassName?: string;
  articleHref?: string;
  articleLabel?: string;
  ctaHref: string;
  ctaLabel: string;
  gameThumbSrc?: string;
  gameThumbAlt?: string;
  transcript?: EducatorVideoTranscript;
  transcriptTestId?: string;
};

const TEACHER_PICK_BADGE = "/images/marketing/educators/featured/teacherPickBadge.webp";

const VOICES: VoiceCard[] = [
  {
    name: "April, 4th Grade Teacher",
    headline: "Warming up with math",
    blurb:
      "In her classroom, April uses Snakebird Math as a quick morning warm-up: students try the puzzle in table groups, come to the board to explain their thinking, and it’s a simple, low-prep activity for early finishers.",
    videoSrc:
      "https://player.vimeo.com/video/1160722406?title=0&byline=0&portrait=0&controls=0&playsinline=1&api=1&player_id=april-vimeo",
    videoTitle: "April educator spotlight video",
    phoneContainerClassName: "-rotate-[1.5deg]",
    badgeSrc: TEACHER_PICK_BADGE,
    badgeClassName:
      "absolute right-0 translate-x-[30%] md:translate-x-1/2 bottom-[12%] w-[52.5%] md:w-[70%] aspect-square z-20 pointer-events-none",
    articleHref: "https://www.weareteachers.com/snakebird-math-game/",
    articleLabel: "Read the full article",
    ctaHref: "/games/snakebird-math",
    ctaLabel: "Play Snakebird Math",
    gameThumbSrc: "/images/marketing/home/games/SnakebirdMath.webp",
    gameThumbAlt: "Snakebird Math",
  },
  {
    name: "Kristen, K–8 District Math Coach",
    headline: "Teach through games",
    blurb:
      "Kristen uses Slice Fractions as a hands-on fraction activity. Students jump into the game and learn by doing with no written directions, working independently, in partners, or in small groups.",
    videoSrc:
      "https://player.vimeo.com/video/1160730558?title=0&byline=0&portrait=0&controls=0&playsinline=1&api=1&player_id=kristen-vimeo",
    videoTitle: "Kristen educator spotlight video",
    phoneContainerClassName: "rotate-[1.5deg]",
    badgeSrc: TEACHER_PICK_BADGE,
    badgeClassName:
      "absolute right-0 translate-x-[30%] md:translate-x-1/2 bottom-[12%] w-[52.5%] md:w-[70%] aspect-square z-20 pointer-events-none",
    articleHref: "https://www.weareteachers.com/slice-fractions-math-game/",
    articleLabel: "Read the full article",
    ctaHref: "/games/mm-slice-fractions",
    ctaLabel: "Play Slice Fractions",
    gameThumbSrc: "/images/marketing/home/games/SliceFractions.webp",
    gameThumbAlt: "Slice Fractions",
  },
  {
    name: "Garcia",
    headline: "Perfect for sub plans",
    blurb:
      "Garcia includes PlayMath.org in substitute lesson plans because it keeps students busy, learning, and engaged. Kids feel like they’re playing a game while building essential math skills, and it’s free with no ads.",
    videoSrc:
      "https://player.vimeo.com/video/1210458417?title=0&byline=0&portrait=0&controls=0&playsinline=1&api=1&player_id=garcia-vimeo",
    videoTitle: "Garcia educator spotlight video",
    phoneContainerClassName: "-rotate-[1deg]",
    ctaHref: "/games",
    ctaLabel: "Explore free math games",
    transcript: GARCIA_VIDEO_TRANSCRIPT,
    transcriptTestId: "garcia-transcript",
  },
];

export function MoreEducatorVoicesSection() {
  return (
    <SectionFrame
      startColor="rgb(var(--cta-yellow-bright))"
      endColor="rgb(var(--cta-orange))"
      flipped={true}
      clipOverflowX={false}
      contentClassName="py-12 md:py-16"
    >
      <div className="text-center max-w-3xl mx-auto mb-10 md:mb-12">
        <h2 className="charlie-kingdom-font text-brand-purple-deep text-3xl md:text-4xl leading-tight">
          More educator voices
        </h2>
        <p className="mt-3 text-ink-slate font-semibold text-base md:text-lg leading-relaxed text-balance">
          Hear how more teachers and coaches use PlayMath.org in the classroom, from morning
          warm-ups to sub plans.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-6 lg:gap-8 items-start">
        {VOICES.map((voice) => (
          <article key={voice.name} className="flex flex-col items-stretch gap-4 text-left">
            <VimeoPhoneVideo
              // w-full on the outer wrapper is required so percentage width resolves for aspect-ratio height.
              outerClassName="flex w-full justify-center"
              phoneContainerClassName={voice.phoneContainerClassName}
              phoneMaxWidthClassName="w-[220px] max-w-full md:w-[240px]"
              src={voice.videoSrc}
              title={voice.videoTitle}
              badgeSrc={voice.badgeSrc}
              badgeClassName={voice.badgeClassName}
              transcript={voice.transcript}
              transcriptTestId={voice.transcriptTestId}
            />

            <div className="flex flex-col gap-2 w-full">
              <p className="text-ink-slate font-extrabold text-base md:text-lg">{voice.name}</p>
              <p className="charlie-kingdom-font text-brand-purple-deep text-xl md:text-2xl leading-tight">
                {voice.headline}
              </p>
              <p className="text-ink-slate/90 font-semibold text-sm md:text-base leading-relaxed">
                {voice.blurb}
              </p>

              {voice.articleHref ? (
                <Link
                  href={voice.articleHref}
                  className="inline-flex items-center justify-start gap-1 text-brand-purple-deep font-extrabold text-sm hover:underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  {voice.articleLabel} <span aria-hidden="true">→</span>
                </Link>
              ) : null}

              <div
                className={`pt-2 flex items-center w-full ${
                  voice.gameThumbSrc ? "flex-row" : "flex-col items-stretch"
                }`}
              >
                {voice.gameThumbSrc ? (
                  <img
                    src={voice.gameThumbSrc}
                    alt={voice.gameThumbAlt ?? ""}
                    className="w-16 h-16 md:w-20 md:h-20 rounded-2xl border-4 border-black bg-white object-cover -rotate-6 shrink-0 relative z-10 -mr-8"
                  />
                ) : null}
                <Link
                  href={voice.ctaHref}
                  className={`w-full inline-flex items-center justify-center bg-cta-yellow text-black rounded-full border-4 border-black shadow-cta-yellow py-3 charlie-kingdom-font text-base md:text-lg relative z-0 hover:bg-cta-yellow-hover transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-black/20 ${
                    voice.gameThumbSrc ? "pl-12 pr-5" : "px-5"
                  }`}
                >
                  <span className="relative -top-0.5">{voice.ctaLabel}</span>
                </Link>
              </div>
            </div>
          </article>
        ))}
      </div>
    </SectionFrame>
  );
}
